import { CurrencyPipe, DecimalPipe, getCurrencySymbol } from '@angular/common';
import { DEFAULT_CURRENCY_CODE, Inject, Injectable, LOCALE_ID } from '@angular/core';
import { parsePhoneNumberWithError, ParseError } from 'libphonenumber-js';

const minFractionDigits = 0;
const maxFractionDigits = 2;

type Formatter = (value: string) => { formattedValue: string; rawValue: string };
export type ValueMask =
  | 'currency'
  | 'decimal'
  | 'integer'
  | 'lei'
  | 'mio'
  | 'percent'
  | 'phone'
  | 'postalCode'
  | 'taxIdNumber'
  | 'taxNumber'
  | 'vatin'
  | 'color'
  | 'radius';

@Injectable({
  providedIn: 'root',
})
export class ValueFormatterService {
  private formatters = new Map<ValueMask, Formatter>([
    ['currency', this.currencyFormatter.bind(this)],
    ['decimal', this.decimalFormatter.bind(this)],
    ['integer', this.integerFormatter.bind(this)],
    ['lei', this.leiFormatter.bind(this)],
    ['mio', this.mioFormatter.bind(this)],
    ['percent', this.percentFormatter.bind(this)],

    ['phone', this.phoneFormatter.bind(this)],
    ['postalCode', this.postalCodeFormatter.bind(this)],
    ['taxIdNumber', this.taxIdNumber.bind(this)],
    ['taxNumber', this.taxNumber.bind(this)],
    ['vatin', this.vatin.bind(this)],
    ['color', this.colorFormatter.bind(this)],
    ['radius', this.radiusFormatter.bind(this)],
  ]);

  constructor(@Inject(LOCALE_ID) public localeId: string, @Inject(DEFAULT_CURRENCY_CODE) public currencyCode: string) {}

  public format(type: ValueMask, value: unknown): ReturnType<Formatter> {
    if (value === null) {
      return { formattedValue: '', rawValue: null };
    }
    return this.formatters.get(type)(this.cleanValue(value));
  }

  public isCharAllowed(char: string, mask: ValueMask): boolean {
    // accept comma as a decimal separator for convenience
    const decimal = /^[0-9.,]$/;
    const digit = /^[0-9]$/;
    const alphanum = /^[0-9A-Za-z]$/;
    const phone = /^[0-9\+]$/;
    const hex = /^[0-9A-Fa-f]$/; // Hexadecimal
    const masks = new Map<ValueMask, (key: string) => boolean>([
      ['currency', (key) => digit.test(key)],
      ['decimal', (key) => decimal.test(key)],
      ['integer', (key) => digit.test(key)],
      ['lei', (key) => alphanum.test(key)],
      ['mio', (key) => decimal.test(key)],
      ['percent', (key) => decimal.test(key)],
      ['phone', (key) => phone.test(key)],
      ['postalCode', (key) => alphanum.test(key) || /\s/.test(key)],
      ['taxIdNumber', (key) => digit.test(key)],
      ['taxNumber', (key) => digit.test(key)],
      ['vatin', (key) => alphanum.test(key)],
      ['color', (key) => hex.test(key)], // Only allow hex chars for color
      ['radius', (key) => digit.test(key)],
    ]);

    if (!masks.has(mask)) {
      return true;
    }

    return masks.get(mask)(char);
  }

  private colorFormatter(value: string): ReturnType<Formatter> {
    const formattedValue = value.startsWith('#') ? value : `#${value}`;
    return { formattedValue, rawValue: formattedValue };
  }

  private radiusFormatter(value: string): ReturnType<Formatter> {
    const numericValue = value.replace(/[^0-9]/g, ''); // Only allow numbers
    const formattedValue = numericValue ? `${numericValue}px` : '';
    return { formattedValue, rawValue: numericValue }; // Send only numeric value back as raw
  }

  private postalCodeFormatter(value: string): ReturnType<Formatter> {
    // Allow letters, numbers, and spaces for UK postcodes
    const cleanValue = value.replace(/[^A-Za-z0-9\s]/g, '');
    return { formattedValue: cleanValue, rawValue: cleanValue };
  }

  private cleanValue(value: unknown): string {
    if (!value && value !== 0) {
      return '';
    }
    return `${value}`.trim();
  }

  private decimalFormatter(
    value: string,
    minimumFractionDigits: number = minFractionDigits,
    maximumFractionDigits: number = maxFractionDigits,
  ): ReturnType<Formatter> {
    const rawValue = this.normalizeValue(value, minimumFractionDigits, maximumFractionDigits);

    return {
      formattedValue: new DecimalPipe(this.localeId).transform(rawValue),
      rawValue,
    };
  }

  private integerFormatter(value: string): ReturnType<Formatter> {
    return this.decimalFormatter(value, 0, 0);
  }

  /**
   * Legal Entity Identifier formatter
   *
   * Formats value as 'xxxx xx xxxxxxxxxxxx xx', e.g. '5493 00 84UKLVMY22DS 16'.
   *
   * If value is of incorrect length or contains incorrect characters
   * no formatting is applied.
   */
  private leiFormatter(value: string): ReturnType<Formatter> {
    let formattedValue = value;
    let rawValue = value;

    if (value.length >= 20 && value.length <= 23) {
      const regexp = /([A-Z\d]{4}) ?([A-Z\d]{2}) ?([A-Z\d]{12}) ?(\d{2})/g;
      const match = regexp.exec(value);

      if (match) {
        formattedValue = `${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
        rawValue = rawValue.replace(/ /g, '');
      }
    }

    return {
      formattedValue,
      rawValue,
    };
  }

  private normalizeValue(
    value: string,
    minimumFractionDigits: number = minFractionDigits,
    maximumFractionDigits: number = maxFractionDigits,
  ): string {
    // accept comma as a decimal separator for convenience
    const v: number = parseFloat(`${value}`.replace(',', '.'));

    if (isNaN(v)) {
      return '';
    }

    // enforcing 'en' so dots are used for fraction marker
    return Number(v).toLocaleString('en', {
      minimumFractionDigits,
      maximumFractionDigits,
      useGrouping: false,
    });
  }

  private percentFormatter(value: string): ReturnType<Formatter> {
    const { rawValue, formattedValue } = this.decimalFormatter(value);

    if (formattedValue) {
      return {
        formattedValue: `${formattedValue}%`,
        rawValue,
      };
    }

    return {
      formattedValue: '',
      rawValue: '',
    };
  }

  private currencyFormatter(value: string): ReturnType<Formatter> {
    // Use decimalFormatter to support decimal places (up to 2 decimal places)
    // This allows currency amounts like 1,000,000.50 instead of just integers
    const { rawValue } = this.decimalFormatter(value);

    return {
      formattedValue: new CurrencyPipe(this.localeId, this.currencyCode).transform(
        rawValue,
        this.currencyCode,
        'symbol',
        '1.2-2', // Support 0-2 decimal places (was '1.0-0' for integers only)
        this.localeId,
      ),
      rawValue,
    };
  }

  /**
   * Tax ID formatter
   *
   * Formats value as 'xx xxx xxx xxx', e.g. '12 345 678 901'.
   *
   * If value is of incorrect length or contains incorrect characters
   * no formatting is applied.
   */
  private taxIdNumber(value: string): ReturnType<Formatter> {
    let formattedValue = value;

    if (value.length === 11) {
      const regexp = /(\d{2})(\d{3})(\d{3})(\d{3})/g;
      const match = regexp.exec(value);

      if (match) {
        formattedValue = `${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
      }
    }

    return {
      formattedValue,
      rawValue: value,
    };
  }

  /**
   * Tax number formatter
   *
   * Formats value as 'xx / xxx / xxxxx' (10-digit tax number)
   * or 'xx / xxx / xxxxxx' (11-digit tax number).
   *
   * Unified federal format of 'BBxx0xxxxxxxx' where BB is Bundesland number
   * is not supported.
   *
   * If value is of incorrect length or contains incorrect characters
   * no formatting is applied.
   */
  private taxNumber(value: string): ReturnType<Formatter> {
    let formattedValue = value;

    if ([10, 11].includes(value.length)) {
      const regexp = /(\d{2})(\d{3})(\d{5,6})/g;
      const match = regexp.exec(value);

      if (match) {
        formattedValue = `${match[1]} / ${match[2]} / ${match[3]}`;
      }
    }

    return {
      formattedValue,
      rawValue: value,
    };
  }

  /**
   * VAT identificaton number formatter
   *
   * Formats value as 'yy xxx xxx xxx', e.g. 'DE 111 222 333'.
   *
   * If value is of incorrect length or contains incorrect characters
   * no formatting is applied.
   */
  private vatin(value: string): ReturnType<Formatter> {
    let formattedValue = value;

    if (value.length === 11) {
      const regexp = /([A-Z]{2})(\d{3})(\d{3})(\d{3})/g;
      const match = regexp.exec(value);

      if (match) {
        formattedValue = `${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
      }
    }

    return {
      formattedValue,
      rawValue: value,
    };
  }

  /**
   * Mio. number formatter
   *
   * Formats value as '{currency} value Mio.', e.g. '€ 0.75 Mil.' or '0.75 Mio. €'.
   *
   * If value contains incorrect characters no formatting is applied.
   */
  private mioFormatter(value: string): ReturnType<Formatter> {
    let formattedValue = value;

    if (value) {
      const { rawValue } = this.decimalFormatter(value);
      const intermediateValue = new CurrencyPipe(this.localeId, this.currencyCode).transform(rawValue);
      const suffix = this.localeId === 'de' ? 'Mio.' : 'Mil.';

      // Due to different currency sign side on various locale, use regex to place the suffix just before the digits
      const currencySymbol = getCurrencySymbol(this.currencyCode, 'narrow', this.localeId);
      const pattern = new RegExp('^' + currencySymbol);

      if (pattern.test(intermediateValue)) {
        formattedValue = `${intermediateValue} ${suffix}`;
      } else {
        formattedValue = intermediateValue.replace(/^([\d|,|\.]*)/, `$1 ${suffix}`);
      }
    }

    return {
      formattedValue,
      rawValue: value,
    };
  }

  /**
   * Phone number formatter
   *
   * If value is not parseable as a phone number no formatting is applied.
   */
  private phoneFormatter(value: string): ReturnType<Formatter> {
    let formattedValue = value;

    try {
      const phoneNumber = parsePhoneNumberWithError(value);

      // isValid() vs isPossible(): https://github.com/catamphetamine/libphonenumber-js#using-phone-number-validation-feature
      if (phoneNumber.isValid()) {
        formattedValue = phoneNumber.formatInternational();
      }
    } catch (error) {
      if (error instanceof ParseError) {
        // swallow it
      } else {
        throw error;
      }
    }

    return {
      formattedValue,
      rawValue: formattedValue.replace(/ /g, ''),
    };
  }
}
